import React from "react"
import { Helmet } from "react-helmet-async"
import { Head, usePage } from "@inertiajs/react"
import { METADATA_WIDTH, buildImageUrl } from "../utils/imageUrl"

/**
 * Social scrapers (Facebook, X, LinkedIn, etc.) fetch og:image/twitter:image
 * from their own servers, so the URL must be absolute. Derive the origin from
 * the current request's absolute URL so it stays correct across localhost,
 * staging, and production without a hardcoded domain. Already-absolute values
 * are left untouched.
 */
const toAbsoluteUrl = (baseAbsoluteUrl, path) => {
    if (/^https?:\/\//i.test(path)) {
        return path
    }

    let origin = ""

    if (baseAbsoluteUrl) {
        try {
            origin = new URL(baseAbsoluteUrl).origin
        } catch {
            origin = ""
        }
    }

    if (!origin && typeof window !== "undefined") {
        origin = window.location.origin
    }

    return `${origin}${path.startsWith("/") ? "" : "/"}${path}`
}

export const Seo = ({
    title = "",
    description = "",
    image = "",
    type = "website",
    robots = "",
    schemas = [],
}) => {
    const cleanDesc = description?.replace(/"/g, "")

    // `canonicalUrl` is a shared prop built server-side (see
    // App\Actions\SEO\BuildCanonicalUrlAction) so the rule for which query
    // params survive into the canonical is testable and lives in one place.
    // Ziggy's shared `location` is the current absolute URL sans query; it is
    // kept as the origin for absolutising the share image, and as the canonical
    // fallback. Both are available during SSR, so these tags render server-side
    // too; window.location only exists once hydrated in the browser.
    const { props } = usePage()
    const { ziggy, canonicalUrl: sharedCanonicalUrl } = props

    const baseCanonicalUrl =
        ziggy?.location ||
        (typeof window !== "undefined"
            ? window.location.origin + window.location.pathname
            : "")

    const canonicalUrl = sharedCanonicalUrl || baseCanonicalUrl

    // Route the share image through the CDN before absolutising. Callers pass the
    // raw origin URL, which would otherwise send unfurlers to the unresized
    // 200-490 KiB original. Done here rather than at each call site so every
    // page benefits and none can forget. buildImageUrl passes through anything
    // it cannot rewrite (including the local default), and toAbsoluteUrl leaves
    // already-absolute URLs alone, so the composition is safe for every input.
    const seoImage = toAbsoluteUrl(
        baseCanonicalUrl,
        buildImageUrl(image, { width: METADATA_WIDTH }) ||
            "/images/default-share.jpg"
    )

    return (
        <>
            <Head>
                <title>{title}</title>
            </Head>

            <Helmet
                htmlAttributes={{ lang: "en" }}
                meta={[
                    // Page-level robots directive, e.g. "noindex, follow" for
                    // the seller silo's kept-out-of-search pages. Rendered
                    // only when a page passes it; Helmet emits it during SSR
                    // like the rest of this array, so the tag is in the
                    // initial document. Distinct from the environment-wide
                    // noindex in app.blade.php, which only guards
                    // non-production hosts.
                    ...(robots ? [{ name: "robots", content: robots }] : []),
                    {
                        name: "twitter:card",
                        content: "summary_large_image",
                    },
                    {
                        name: "twitter:title",
                        content: title,
                    },
                    {
                        name: "twitter:description",
                        content: cleanDesc,
                    },
                    {
                        name: "twitter:image",
                        content: seoImage,
                    },
                    {
                        name: `description`,
                        content: cleanDesc,
                    },
                    {
                        property: "og:locale",
                        content: "en_US",
                    },
                    {
                        property: "og:type",
                        content: type,
                    },
                    {
                        property: "og:title",
                        content: title,
                    },
                    {
                        property: "og:description",
                        content: cleanDesc,
                    },
                    {
                        property: "og:url",
                        content: canonicalUrl,
                    },
                    {
                        property: "og:site_name",
                        content: "FirearmLand",
                    },
                    {
                        property: "og:image",
                        content: seoImage,
                    },
                ]}
                link={[
                    {
                        rel: "canonical",
                        href: canonicalUrl,
                    },
                    {
                        href: "/favicon.ico",
                        rel: "shortcut icon",
                    },
                    {
                        rel: "apple-touch-icon",
                        sizes: "57x57",
                        href: "/apple-icon-57x57.png",
                    },
                    {
                        rel: "apple-touch-icon",
                        sizes: "60x60",
                        href: "/apple-icon-60x60.png",
                    },
                    {
                        rel: "apple-touch-icon",
                        sizes: "76x76",
                        href: "/apple-icon-76x76.png",
                    },
                    {
                        rel: "apple-touch-icon",
                        sizes: "114x114",
                        href: "/apple-icon-114x114.png",
                    },
                    {
                        rel: "apple-touch-icon",
                        sizes: "120x120",
                        href: "/apple-icon-120x120.png",
                    },
                    {
                        rel: "apple-touch-icon",
                        sizes: "144x144",
                        href: "/apple-icon-144x144.png",
                    },
                    {
                        rel: "apple-touch-icon",
                        sizes: "152x152",
                        href: "/apple-icon-152x152.png",
                    },
                    {
                        rel: "apple-touch-icon",
                        sizes: "180x180",
                        href: "/apple-icon-180x180.png",
                    },
                    {
                        rel: "icon",
                        type: "image/png",
                        sizes: "192x192",
                        href: "/android-icon-192x192.png",
                    },
                    {
                        rel: "icon",
                        type: "image/png",
                        sizes: "32x32",
                        href: "/favicon-32x32.png",
                    },
                    {
                        rel: "icon",
                        type: "image/png",
                        sizes: "96x96",
                        href: "/favicon-96x96.png",
                    },
                    {
                        rel: "icon",
                        type: "image/png",
                        sizes: "16x16",
                        href: "/favicon-16x16.png",
                    },
                ]}
            >
                {schemas?.filter(Boolean).map((schema, index) => (
                    <script key={`schema-${index}`} type="application/ld+json">
                        {JSON.stringify(schema)}
                    </script>
                ))}
            </Helmet>
        </>
    )
}
